import {BaseController, EcomPlatformViewerScriptContext} from '@wix/wixstores-client-storefront-sdk';
import {ControllerParams} from '@wix/yoshi-flow-editor';
import {toMonitorError} from '../../domain/utils/errorMonitor.utils';
import {PaymentRequestControllerProps, PaymentRequestErrorCode} from '../../types/paymentRequestApp.types';
import {PaymentRequestSettingsStore} from '../../domain/stores/paymentRequest/PaymentRequestSettingsStore';
import {PaymentRequestSettingsService} from '../../domain/services/paymentRequest/PaymentRequestSettingsService';
import {PaymentRequestPaymentStore} from '../../domain/stores/paymentRequest/PaymentRequestPaymentStore';
import {PaymentRequestStore} from '../../domain/stores/paymentRequest/PaymentRequestStore';
import {PaymentRequestService} from '../../domain/services/paymentRequest/PaymentRequestService';
import {PaymentRequestNavigationService} from '../../domain/services/paymentRequest/PaymentRequestNavigationService';
import {PaymentRequestNavigationStore} from '../../domain/stores/paymentRequest/PaymentRequestNavigationStore';
import {PaymentRequestBIService} from '../../domain/services/paymentRequest/PaymentRequestBIService';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {SPECS} from '../../common/constants';

export class PaymentRequestController extends BaseController {
  protected paymentRequestStore!: PaymentRequestStore;
  protected paymentRequestSettingsStore!: PaymentRequestSettingsStore;
  protected paymentStore!: PaymentRequestPaymentStore;
  protected navigationStore!: PaymentRequestNavigationStore;
  protected translationPromise!: Promise<Record<string, string>>;
  protected viewerContext!: ViewerContext;
  private initServiceFunctions!: (() => void)[];

  constructor(controllerParams: ControllerParams) {
    super(controllerParams);
    this.viewerContext = new ViewerContext(
      controllerParams.controllerConfig,
      controllerParams.controllerConfig.essentials.experiments.enabled(SPECS.MoveCheckoutToViewerContext)
        ? undefined
        : this.siteStore
    );
    this.setStores(controllerParams);
    this.loadLocaleDataSetTranslations(controllerParams);
  }

  private loadLocaleDataSetTranslations(controllerParams: ControllerParams) {
    this.translationPromise = this.fetchLocaleDataSetTranslations(controllerParams);
  }

  private async fetchLocaleDataSetTranslations(controllerParams: ControllerParams) {
    const {language} = controllerParams.flowAPI.translations.config;
    let translations: Record<string, string>;
    try {
      translations = await import(`@wix/locale-dataset-data/resources/translations/messages_${language}.json`);
    } catch (e) {
      this.flowAPI.panoramaClient.errorMonitor().reportError(...toMonitorError(e));
      translations = (await import(
        `@wix/locale-dataset-data/resources/translations/messages_en.json`
      )) as unknown as Record<string, string>;
    }
    return {...controllerParams.flowAPI.translations.all, ...translations};
  }

  private async setLocaleDatasetTranslations() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.setProps({_translations: await this.translationPromise});
  }

  private setStores({flowAPI, controllerConfig, appData}: ControllerParams) {
    const siteStore = this.siteStore;
    const viewerContext = this.viewerContext;
    const eCommerceSettingsService = (appData?.context as EcomPlatformViewerScriptContext).eCommerceSettingsService;
    const isSSR = controllerConfig.wixCodeApi.window.rendering.env === 'backend';
    const updateComponent = this.updateComponent.bind(this);

    const navigationService = new PaymentRequestNavigationService({siteStore, controllerConfig, viewerContext});
    const paymentRequestSettingsService = new PaymentRequestSettingsService({
      viewerContext,
      eCommerceSettingsService,
    });
    const paymentRequestService = new PaymentRequestService({siteStore, flowAPI, viewerContext, controllerConfig});
    const paymentRequestBIService = new PaymentRequestBIService(
      {siteStore, paymentRequestSettingsService, viewerContext},
      controllerConfig
    );

    this.paymentRequestSettingsStore = new PaymentRequestSettingsStore({
      paymentRequestSettingsService,
    });

    this.paymentStore = new PaymentRequestPaymentStore({
      flowAPI,
      paymentRequestService,
      paymentRequestSettingsService,
      navigationService,
      isSSR,
      updateComponent,
    });

    this.paymentRequestStore = new PaymentRequestStore({
      flowAPI,
      paymentRequestService,
      navigationService,
      updateComponent,
      paymentRequestBIService,
    });

    this.navigationStore = new PaymentRequestNavigationStore({
      navigationService,
      paymentRequestService,
      siteStore,
      viewerContext,
      updateComponent,
      isSSR,
      controllerConfig,
    });

    this.initServiceFunctions = [
      async () => {
        await paymentRequestService.init();
      },
      () => paymentRequestSettingsService.init(),
      () => navigationService.load(),
    ];
  }

  private readonly updateComponent = () => {
    const props: PaymentRequestControllerProps = {
      isLoading: false,
      paymentRequestState: this.navigationStore.getPaymentRequestState(),
      paymentRequestStore: this.paymentRequestStore.toProps(),
      paymentRequestSettingsStore: this.paymentRequestSettingsStore.toProps(),
      paymentStore: this.paymentStore.toProps(),
      navigationStore: this.navigationStore.toProps(),
    };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.setProps(props);
  };

  public readonly load = async (): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.setProps({isLoading: true, fitToContentHeight: true});
    void this.loadData();
    return Promise.resolve();
  };

  public readonly loadData = async (): Promise<void> => {
    void this.setLocaleDatasetTranslations();
    if (this.viewerContext.isSSR()) {
      return;
    }

    try {
      await Promise.all(this.initServiceFunctions.map((fn) => fn()));
      this.navigationStore.calculateInitialPaymentRequestStatus();
      this.paymentRequestStore.sendPaymentRequestPageStartLoadBIEvent();
    } catch (e) {
      /* istanbul ignore next */
      this.flowAPI.panoramaClient.errorMonitor().reportError(...toMonitorError(e, 'PaymentRequestController load'));
      /* istanbul ignore next */
      this.onLoadFailure();
    }

    this.updateComponent();
  };

  /* istanbul ignore next */
  private readonly onLoadFailure = () => {
    this.paymentRequestStore.setPaymentRequestError(PaymentRequestErrorCode.generalError);
  };

  // eslint-disable-next-line @typescript-eslint/require-await
  public readonly init = async (): Promise<void> => {
    return this.load();
  };

  /* istanbul ignore next */
  public getFreeTexts(): string[] {
    return [];
  }

  /* istanbul ignore next */
  public exports() {
    return {};
  }

  /* istanbul ignore next */

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onStyleUpdate(_styleParams: /* eslint-disable @typescript-eslint/ban-types */ {}) {}
}
